import {
  Box,
  Chip,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { extractReason } from 'views/TirePressure/util';

import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
  TpmsLeakageNotification,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const NotificationsTable = ({
  enableDarkTheme,
  notifications,
  makeModelDetails,
  tirePatterns,
  customerId,
  leakageNotifications,
  tireCodes,
}: {
  enableDarkTheme: boolean;
  notifications: any[];
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
  leakageNotifications: TpmsLeakageNotification[];
  tireCodes: Record<string, string>;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const fontColor = palette.textPrimary;
  const headerCellSx: SxProps = {
    color: palette.white,
    bgcolor: palette.tableHeader,
    border: 'none',
    fontSize: '12px',
  };

  const getStatusColor = (status: boolean) => {
    return status ? 'error' : 'success';
  };

  const formatNotificationsForVehicleInsight = () => {
    return notifications.map((row) => {
      const reason = extractReason(row?.rule?.ruleName);
      const tagName = row.rule?.ruleConditionAssociations[0]?.tagName ?? '';
      const parts = tagName.split('_');
      const rawTireCode = String(parts[2] || '').toLowerCase();
      const tireCode = '0x' + rawTireCode.replace('0x', '').toUpperCase();

      return {
        ...row,
        formatted: {
          alertStartTime: convertUtcToDisplayTime(
            row.alertStartTs,
            preferredTimeDisplayMode,
            timezone,
            DATE_TIME_FORMAT
          ),
          reason: reason.toUpperCase(),
          tireName: tireCodes[tireCode] || 'N/A',
          statusLabel: row.isActive
            ? reason === 'inflate'
              ? 'Info'
              : 'Active'
            : 'Solved',
          statusColor: getStatusColor(row.isActive),
          statusBgColor: row.isActive
            ? reason === 'inflate'
              ? '#3666EC'
              : '#EB5757'
            : '#009161',
          resolvedTime:
            reason !== 'inflate' && !row.isActive
              ? convertUtcToDisplayTime(
                  row.updatedAt,
                  preferredTimeDisplayMode,
                  timezone,
                  'DD MMM YYYY hh:mm A'
                )
              : null,
        },
      };
    });
  };

  const formatLeakageNotifications = () => {
    return leakageNotifications.map((row) => {
      const rawCode = String(
        row.tireCode || (row as any).tire || ''
      ).toLowerCase();
      const rowTireCode = '0x' + rawCode.replace('0x', '').toUpperCase();
      return {
        ...row,
        formatted: {
          alertStartTime: convertUtcToDisplayTime(
            row.alertStartTs,
            preferredTimeDisplayMode,
            timezone,
            DATE_TIME_FORMAT
          ),
          reason: row.leakType.toUpperCase(),
          tireName:
            row.tirePosition ||
            (row as any).side_of_truck ||
            tireCodes[rowTireCode] ||
            'N/A',
          statusLabel: row.isActive ? 'Active' : 'Solved',
          statusColor: row.isActive ? 'error' : 'success',
          statusBgColor: row.isActive ? '#EB5757' : '#009161',
          resolvedTime: !row.isActive
            ? convertUtcToDisplayTime(
                row.alertEndTs || row.updatedAt,
                preferredTimeDisplayMode,
                timezone,
                'DD MMM YYYY hh:mm A'
              )
            : null,
        },
      };
    });
  };

  let formattedNotifications = formatNotificationsForVehicleInsight();

  const formattedLeakageNotifications = formatLeakageNotifications();

  formattedNotifications = [
    ...formattedNotifications,
    ...formattedLeakageNotifications,
  ];
  return (
    <Box
      sx={{
        marginTop: '10px',
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headerCellSx,
                  pl: 3,
                }}
              >
                {t('date_time_2')}
              </TableCell>
              <TableCell
                sx={{
                  ...headerCellSx,
                }}
              >
                {t('notification_type')}
              </TableCell>
              <TableCell
                sx={{
                  ...headerCellSx,
                }}
              >
                Tire
              </TableCell>
              <TableCell
                sx={{
                  ...headerCellSx,
                }}
              >
                {t('status')}
              </TableCell>
              <TableCell
                sx={{
                  ...headerCellSx,
                }}
              >
                {t('last_updated_2')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formattedNotifications.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  '& td': {
                    border: 'none',
                    borderBottom:
                      index !== formattedNotifications.length - 1
                        ? '1px solid #2F445E'
                        : 'none',
                  },
                }}
              >
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: fontColor,
                    }}
                  >
                    {row.formatted.alertStartTime}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: fontColor,
                    }}
                  >
                    {row.formatted.reason}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: fontColor,
                    }}
                  >
                    {row.formatted.tireName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.formatted.statusLabel}
                    color={row.formatted.statusColor}
                    sx={{
                      fontWeight: '600',
                      minWidth: '80px',
                      fontSize: '12px',
                      '&.MuiChip-colorError': {
                        bgcolor: row.formatted.statusBgColor,
                        color: 'white',
                      },
                      '&.MuiChip-colorSuccess': {
                        bgcolor: row.formatted.statusBgColor,
                        color: 'white',
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: fontColor,
                    }}
                  >
                    {row.formatted.resolvedTime}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
